import {
  IconFileUpload,
  IconPhoto,
  IconTag,
  IconX,
} from '@tabler/icons-react'
import {
  App,
  Flex,
  Input,
  Tooltip,
  Typography,
} from 'antd/es'
import classNames from 'classnames'
import { uniq } from 'lodash'
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDebounce } from 'react-use/lib'

import DropZone from '@/components/fields/drop-zone'
import EmojiPicker from '@/components/shared/emoji-picker'
import ImageUploader from '@/components/shared/image-uploader'
import { VaultModal } from '@/components/shared/vault/vault-modal'
import { useAddMedia } from '@/hooks/use-add-media'
import {
  Media,
  MediaType,
} from '@/model/media'
import { updateMessageDraft } from '@/redux/message-draft'
import {
  useAppDispatch,
  useAppSelector,
} from '@/redux/utils'
import { rejectUndefinedAndNull } from '@/utils/array'
import { formatPrice } from '@/utils/formatter'

import { selectMessageDraft } from '../selector'

import s from './post-message-input.module.css'
import PostPhotoList from './post-photo-list'
import PostVideoPreview from './post-video-preview'
import usePostPriceModal from './use-post-price-modal'

const { TextArea } = Input

const MEDIA_FILE_ACCEPT = 'video/*,audio/*,image/*'
const { Text } = Typography

interface TextAreaRef extends HTMLTextAreaElement {
  resizableTextArea: {
    textArea: HTMLTextAreaElement
  }
}

function iconStatusClassName(enabled: boolean) {
  return classNames('icon-button', { ['icon-button-disabled']: !enabled })
}

export interface PostMessageInputProps {
  creatorId: number
  creatorPlatformUserId: number
  fanUserId: number
}

export default function PostMessageInput({
  creatorId,
  fanUserId,
  creatorPlatformUserId,
}: PostMessageInputProps) {
  const { text, medias, version, price, sendMediaType, cover } = useAppSelector((state) =>
    selectMessageDraft(state, creatorId, fanUserId),
  )

  const { message } = App.useApp()

  const contentRef = useRef<TextAreaRef>(null)
  const [tempText, setTempText] = useState('')
  const [vaultOpen, setVaultOpen] = useState(false)
  const dispatch = useAppDispatch()

  useEffect(() => {
    setTempText(text)
  }, [text, version])

  const accept = useMemo(() => {
    const type = medias.length > 0 ? medias[0].type : undefined
    let accept = ''
    if (type === undefined) {
      accept = MEDIA_FILE_ACCEPT
    } else if (type === MediaType.Image) {
      accept = 'image/*'
    } else if (type === MediaType.Video) {
      accept = 'video/*'
    }
    return accept
  }, [medias])

  const onAddMedia = useAddMedia({
    creatorId,
    fanUserId,
  })

  const onSelectFiles = useCallback(
    async (files: File[] | FileList | null) => {
      if (!files || files.length === 0) return
      const sendMediaType = files[0].type.startsWith('video')
        ? MediaType.Video
        : MediaType.Image
      dispatch(
        updateMessageDraft({
          creatorId,
          fanUserId,
          changes: { sendMediaType },
        }),
      )
      await onAddMedia(files)
    },
    [creatorId, dispatch, fanUserId, onAddMedia],
  )

  const onAddMediasFromVault = useCallback(
    (newMedias: Media[]) => {
      const nextMedias = medias.concat(newMedias)
      const selectedTypes = uniq(nextMedias.map((media) => media.type))

      if (selectedTypes.length > 1) {
        void message.info('Please select only images or only videos')
        return
      }

      if (nextMedias.length > 0 && nextMedias[0].type === MediaType.Video) {
        dispatch(
          updateMessageDraft({
            creatorId,
            fanUserId,
            changes: {
              sendMediaType: MediaType.Video,
              medias: [nextMedias[0]],
              cover: nextMedias[0].thumbnail,
            },
          }),
        )
        return
      }

      dispatch(
        updateMessageDraft({
          creatorId,
          fanUserId,
          changes: {
            sendMediaType: MediaType.Image,
            medias: [...medias, ...newMedias],
          },
        }),
      )
    },
    [creatorId, dispatch, fanUserId, medias, message],
  )

  const insertTextToContent = useCallback(
    (textToInsert: string) => {
      if (contentRef.current) {
        const textArea = contentRef.current
        const Input = textArea.resizableTextArea.textArea
        const start = Input.selectionStart
        const end = Input.selectionEnd
        dispatch(
          updateMessageDraft({
            creatorId,
            fanUserId,
            changes: {
              text: tempText
                ? tempText.substring(0, start) +
                textToInsert +
                tempText.substring(end)
                : textToInsert,
            },
          }),
        )
      }
    },
    [creatorId, dispatch, fanUserId, tempText],
  )

  useDebounce(
    () => {
      try {
        dispatch(
          updateMessageDraft({
            creatorId,
            fanUserId,
            keepVersion: true,
            changes: {
              text: tempText,
            },
          }),
        )
      } catch (err) {
        console.error(err)
      }
    },
    500,
    [tempText, dispatch],
  )

  const onSetPrice = useCallback(
    (nextPrice: number | undefined) => {
      dispatch(
        updateMessageDraft({
          creatorId,
          fanUserId,
          changes: { price: nextPrice },
        }),
      )
    },
    [creatorId, dispatch, fanUserId],
  )

  const onTextChange = useCallback(
    (value: ChangeEvent<HTMLTextAreaElement> | string) => {
      const newText = typeof value === 'string' ? value : value.target.value
      setTempText(newText)
    }, [])

  const handleSetCover = useCallback(
    (nextCover: string | undefined) => {
      dispatch(
        updateMessageDraft({
          creatorId,
          fanUserId,
          changes: { cover: nextCover },
        }),
      )
    },
    [creatorId, dispatch, fanUserId],
  )

  const [blockModal, openBlockModal] = usePostPriceModal({ onSetPrice })
  const videoSelected = sendMediaType === MediaType.Video && !!medias[0]

  return (
    <Flex vertical gap={8}>
      {price !== undefined && (
        <Flex align="center" justify="flex-end" gap={4}>
          <span>Price: {formatPrice()(price || 0)}</span>
          <IconX
            className="icon-button"
            size={14}
            onClick={() => onSetPrice(undefined)}
          />
        </Flex>
      )}
      <Flex>
        {sendMediaType === MediaType.Image && (
          <PostPhotoList creatorId={creatorId} fanUserId={fanUserId} />
        )}
        {sendMediaType === MediaType.Video && medias.length > 0 && (
          <PostVideoPreview
            creatorId={creatorId}
            fanUserId={fanUserId}
            media={medias[0]}
          />
        )}
      </Flex>
      <DropZone accept={MEDIA_FILE_ACCEPT} onDropFiles={onSelectFiles} disabled={videoSelected}>
        <TextArea
          className={s.TextArea}
          placeholder="Say something ..."
          autoSize={{ minRows: 4, maxRows: 10 }}
          ref={contentRef}
          value={tempText}
          onChange={onTextChange}
        />
        <EmojiPicker
          className={s.EmojiPicker}
          onEmojiSelected={insertTextToContent}
        />
        {sendMediaType === MediaType.Video && medias[0]?.src && (
          <Flex vertical align="flex-start" className={s.VideoCover}>
            {/* TODO: implement component */}
            <Text strong>Video cover image</Text>
            <ImageUploader creatorId={creatorId} value={cover} onChange={handleSetCover} />
          </Flex>
        )}
        <div className={s.FooterOperations}>
          <Tooltip title="Add media">
            <label className={s.AddMediaButton}>
              <IconFileUpload className={iconStatusClassName(!videoSelected)} />
              <input
                accept={accept}
                multiple={true}
                value=""
                type="file"
                onChange={(e) => onSelectFiles(e.target.files)}
              />
            </label>
          </Tooltip>
          <Tooltip title="Add media from vault">
            <label>
              <IconPhoto
                className={iconStatusClassName(!videoSelected)}
                onClick={() => setVaultOpen(true)}
              />
            </label>
          </Tooltip>
          <Tooltip title="Set price">
            <label>
              <IconTag
                className={iconStatusClassName(true)}
                onClick={() => openBlockModal()}
              />
            </label>
          </Tooltip>
        </div>
      </DropZone>
      <VaultModal
        creatorId={creatorId}
        platformUserId={creatorPlatformUserId}
        open={vaultOpen}
        setOpen={setVaultOpen}
        onAddMedias={onAddMediasFromVault}
        excludedMediaUrls={medias.map(({ src }) => src).filter(rejectUndefinedAndNull)}
        maxSelection={9}
      />
      {blockModal}
    </Flex>
  )
}
